<template>
    <div>
        <div class="content">
            <h1>{{ lang('Stats') }}</h1>

            <h2>{{ lang('Event Hours') }}</h2>

            <p>{{ lang('Total hours by event type.') }}</p>

            <div class="block">
                <b-field grouped>
                    <b-field>
                        <b-checkbox v-model="selectedRoles" v-for="role in roles" :key="role" :native-value="role">
                            {{ role }}
                        </b-checkbox>
                    </b-field>
                    <b-field>
                        <b-select v-model="year" @input="yearChanged">
                            <option v-for="year in years" :key="year" :value="year">{{ lang(year) }}</option>
                        </b-select>
                    </b-field>
                    <b-field>
                        <b-select v-model="month" @input="loadAsyncData">
                            <option v-for="month in months" :key="month.number" :value="month.number">{{ lang(month.name) }}</option>
                        </b-select>
                    </b-field>
                </b-field>
            </div>

            <b-table :data="filteredData" :loading="loading" :default-sort-direction="defaultSortOrder" :default-sort="[sortField, sortOrder]">

                <template slot-scope="props">
                    <b-table-column :label="lang('Timesheet')">
                        <b-dropdown position="is-bottom-right" aria-role="menu">
                            <button class="button is-primary is-small" slot="trigger">
                                <b-icon icon="file-pdf-o"></b-icon>
                                <b-icon icon="caret-down"></b-icon>
                            </button>

                            <b-dropdown-item v-for="month in monthsOfYear(year)" :key="month.number" has-link aria-role="menuitem">
                                <a @click.prevent="ibanPrompt(year, month.name, '/users/timesheet?user_id='+props.row.id+'&year='+year+'&month='+month.number)">
                                    {{ lang(month.name) }}
                                </a>
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-table-column>

                    <b-table-column field="name" :label="lang('Full Name')" sortable>
                        {{ props.row.name }}
                    </b-table-column>

                    <b-table-column field="role" :label="lang('Role')" sortable>
                        {{ props.row.role }}
                    </b-table-column>

                    <b-table-column v-for="(hours, type) in props.row.event_hours" :key="type" :field="type + '_hours'" :label="type" numeric sortable :custom-sort="sort(type)">
                        {{ hours }}
                    </b-table-column>
                </template>
            </b-table>
        </div>
    </div>
</template>

<script>
export default {
    name: "Stats",
    data() {
        return {
            data: [],
            formErrors: {},
            loading: false,
            sortField: "name",
            sortOrder: "asc",
            defaultSortOrder: "asc",
            roles: ["Admin", "Trainer", "Hallenöffner"],
            selectedRoles: [],
            years: [],
            year: 2018,
            month: null
        };
    },
    methods: {
        /*
         * Load async data
         */
        loadAsyncData() {
            let self = this;
            this.loading = true;

            axios
                .get(`/users/stats`, {
                    params: {
                        year: this.year,
                        month: this.month
                    }
                })
                .then(function(response) {
                    self.data = [];
                    response.data.forEach(item => self.data.push(item));
                    self.loading = false;
                })
                .catch(function(error) {
                    self.data = [];
                    self.loading = false;
                    if (error.response.status == 401) {
                        // unauthorized
                        self.logout();
                    } else {
                        self.alertCustomError(error.response.data.message);
                    }
                });
        },
        yearChanged() {
            if (!_.find(this.months, ['number', this.month])) {
                this.month = null;
            }
            this.loadAsyncData();
        },
        /*
         * Type style in relation to the value
         */
        type(value) {
            const number = parseFloat(value);
            if (number < 6) {
                return "is-danger";
            } else if (number >= 6 && number < 8) {
                return "is-warning";
            } else if (number >= 8) {
                return "is-success";
            }
        },
        alertCustomError(message) {
            this.$buefy.dialog.alert({
                title: "Error",
                message: message,
                type: "is-danger",
                hasIcon: true,
                icon: "times-circle"
            });
        },
        logout() {
            window.location.href = "/logout";
        },
        sort(type) {
            return function(a, b, isAsc) {
                a = JSON.parse(JSON.stringify(a.event_hours))[type];
                b = JSON.parse(JSON.stringify(b.event_hours))[type];
                return isAsc ? a - b : b - a;
            };
        },
        monthsOfYear(year) {
            let months = [];
            let baseDate = new Date(Date.UTC(year, 0, 1));
            let now = new Date();
            for(let i = 1; i <= 12; i++)
            {
                if (baseDate > now) {
                    break;
                }
                months.push({'number': i, 'name': baseDate.toLocaleDateString('en', { month: 'long' })});
                baseDate.setMonth(baseDate.getMonth() + 1);
            }
            return months;
        },
        ibanPrompt(year, month, url) {
            var self = this;
            this.$buefy.dialog.prompt({
                title: self.lang('Timesheet') + ' ' + self.lang(month) + ', ' + year,
                message: self.lang('Enter an IBAN'),
                inputAttrs: {
                    placeholder: self.lang('e.g.') + ' ' + 'DE89 3704 0044 0532 0130 00',
                    maxlength: 27,
                    required: false
                },
                trapFocus: true,
                confirmText: self.lang('Create PDF'),
                cancelText: self.lang('Cancel'),
                onConfirm: (value) => {
                    window.open(url + '&iban=' + value, '_blank');
                }
            });
        },
    },
    filters: {
        /**
         * Filter to truncate string, accepts a length parameter
         */
        truncate(value, length) {
            return value.length > length
                ? value.substr(0, length) + "..."
                : value;
        }
    },
    created() {
        this.selectedRoles = this.roles;
        this.year = new Date().getFullYear();
        for (var y = 2018; y <= this.year; y++) {
            this.years.push(y);
        }
    },
    mounted() {
        this.loadAsyncData();
    },
    computed: {
        filteredData() {
            const self = this;
            return _.filter(this.data, function(user) {
                return self.selectedRoles.includes(user.role);
            });
        },
        months() {
            let months = [{number: null, name: 'All Months'}];
            let baseDate = new Date(Date.UTC(this.year, 0, 1));
            let now = new Date();
            for(let i = 1; i <= 12; i++)
            {
                if (baseDate > now) {
                    break;
                }
                months.push({'number': i, 'name': baseDate.toLocaleDateString('en', { month: 'long' })});
                baseDate.setMonth(baseDate.getMonth() + 1);
            }
            return months;
        }
    }
};
</script>
